import { Box, Heading, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useDoc } from './useDoc';
import { useAdminDocsHost } from './host-context';
import { DocsList } from './DocsList';
import { DocsViewer } from './DocsViewer';

export interface DocsRouteProps {
  /** Optional override; when present, takes precedence over :slug param. */
  slug?: string;
}

/**
 * Resolves the active doc from the route param and renders the viewer
 * (or the index list when no slug is present). Permission gating happens
 * here so hosts only have to wrap with auth + host context.
 */
export function DocsRoute({ slug: slugProp }: DocsRouteProps = {}) {
  const params = useParams<{ slug?: string }>();
  const slug = slugProp ?? params.slug;
  const { entry, body } = useDoc(slug);
  const { permissions } = useAdminDocsHost();

  if (!slug) {
    return <DocsList />;
  }

  if (!entry || body === null) {
    return (
      <Box maxW="container.md" mx="auto" px="4" py="6">
        <Heading as="h1" size="lg" mb="2">
          Guide not found
        </Heading>
        <Text color="neutral.600">
          No admin guide exists for <code>{slug}</code>.
        </Text>
      </Box>
    );
  }

  if (!permissions.includes(entry.requiredPermission)) {
    return (
      <Box maxW="container.md" mx="auto" px="4" py="6">
        <Heading as="h1" size="lg" mb="2">
          Not authorized
        </Heading>
        <Text color="neutral.600">
          You don&apos;t have permission to view this guide. Ask your
          organization admin to grant access.
        </Text>
      </Box>
    );
  }

  return <DocsViewer entry={entry} body={body} />;
}
